import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BrainCircuit,
  Check,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Zap,
  Gauge,
  Coins,
  Clock,
  Clock3,
  Layers,
  FileCode2,
  Eye,
  RefreshCw,
  Copy,
  CheckCheck,
  Bot,
  Sparkles,
  Filter,
  Search,
  BookOpen,
  Edit3,
  X,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronUp,
  Shield,
  Send,
} from "lucide-react";
import {
  fetchVaultAgents,
  fetchVaultTelemetry,
  fetchVaultRules,
  fetchVaultFeedback,
  assemblePromptDryRun,
  fetchAiQuotaConfig,
  fetchAiConsumption,
  triageFeedback,
  promoteRule,
  fetchStructuredRules,
} from "../api";
import { MetricCard } from "../components/MetricCard";
import { LoadingState } from "../components/LoadingState";
import { useDashboard } from "../context/DashboardContext";
import { compactNumber } from "../lib/format";
import type {
  GertrudisAgentRow,
  VaultTelemetryPayload,
  VaultRulesPayload,
  HitlFeedbackRow,
  HitlFeedbackSummary,
  GertrudisStructuredRule,
  PromptAssemblyPayload,
  AiQuotaConfig,
  AiConsumptionRow,
  AiQuotaLimit,
} from "../types";


function getProviderInfo(modelName: string): { name: string; className: string } {
  const m = (modelName || "").toLowerCase();
  if (m.includes("claude")) {
    return { name: "Anthropic Claude", className: "provider-claude" };
  }
  if (m.includes("deepseek")) {
    return { name: "DeepSeek", className: "provider-deepseek" };
  }
  return { name: "Google Gemini", className: "provider-gemini" };
}

function numeric(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function findRow(rows: AiConsumptionRow[], model: string): AiConsumptionRow | undefined {
  return rows.find((row) => String(row.modelo) === model || String(row.modelo) === `models/${model}`);
}

function QuotaBar({ label, value, limit }: { label: string; value: number; limit: number | null }) {
  const pct = limit ? Math.min((value / limit) * 100, 100) : 0;
  const tone = pct >= 90 ? "danger" : pct >= 70 ? "warning" : "good";
  return (
    <div className={`quota-line is-${tone}`}>
      <div>
        <span>{label}</span>
        <strong>
          {compactNumber.format(value)} / {limit == null ? "sin tope" : compactNumber.format(limit)}
        </strong>
      </div>
      <i>
        <b style={{ width: `${pct}%` }} />
      </i>
    </div>
  );
}

function ModelQuota({ model, limits, rows }: { model: string; limits: AiQuotaLimit; rows: AiConsumptionRow[] }) {
  const row = findRow(rows, model);
  return (
    <article className="quota-card">
      <div className="quota-card-head">
        <div>
          <Bot size={17} />
          <strong>{model.replace("gemini-", "Gemini ").replace("claude-", "Claude ")}</strong>
        </div>
        <small>
          <Clock3 size={12} /> {row?.ultima_peticion ? new Date(row.ultima_peticion).toLocaleString("es-CL") : "Nunca en el periodo"}
        </small>
      </div>
      <QuotaBar label="RPM · peticiones/min" value={numeric(row?.peticiones_ultimo_min)} limit={limits.RPM} />
      <QuotaBar label="TPM · tokens/min" value={numeric(row?.tokens_ultimo_min)} limit={limits.TPM} />
      <QuotaBar label="RPD · peticiones/día" value={numeric(row?.peticiones_hoy)} limit={limits.RPD} />
    </article>
  );
}

export function PromptVaultPage() {
  const { data } = useDashboard();
  const clients = data?.clients ?? [];

  const validTabs = ["control", "telemetry", "quotas", "rules", "assembly", "feedback"] as const;
  type TabType = typeof validTabs[number];

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") as TabType | null;
  const initialTab = tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : "control";
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);

  const handleTabChange = (t: TabType) => {
    setActiveTab(t);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("tab", t);
      return p;
    });
  };

  // Estados de datos
  const [agents, setAgents] = useState<GertrudisAgentRow[]>([]);
  const [telemetry, setTelemetry] = useState<VaultTelemetryPayload | null>(null);
  const [rules, setRules] = useState<VaultRulesPayload | null>(null);
  const [feedback, setFeedback] = useState<HitlFeedbackRow[]>([]);
  const [feedbackSummary, setFeedbackSummary] = useState<HitlFeedbackSummary | null>(null);
  const [structuredRules, setStructuredRules] = useState<GertrudisStructuredRule[]>([]);
  const [telemDays, setTelemDays] = useState(30);

  // Estados de curación ágil HITL
  const [curationView, setCurationView] = useState<"inbox" | "catalog">("inbox");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [levelFilter, setLevelFilter] = useState("ALL");
  const [clientFilter, setClientFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [expandedInsights, setExpandedInsights] = useState<Record<string, boolean>>({});

  // Modal de Promoción a Regla Estructurada
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [selectedFeedbackForPromotion, setSelectedFeedbackForPromotion] = useState<HitlFeedbackRow | null>(null);
  const [ruleId, setRuleId] = useState("");
  const [ruleDomain, setRuleDomain] = useState("SEO");
  const [ruleLevel, setRuleLevel] = useState("Per-URL SEO");
  const [ruleTitle, setRuleTitle] = useState("");
  const [rulePrecondition, setRulePrecondition] = useState("");
  const [ruleDiagnosis, setRuleDiagnosis] = useState("");
  const [ruleAction, setRuleAction] = useState("");
  const [ruleNoAplicaSi, setRuleNoAplicaSi] = useState("");
  const [ruleClient, setRuleClient] = useState("ALL");
  const [ruleStatus, setRuleStatus] = useState<"ACTIVA" | "A_PRUEBA">("ACTIVA");
  const [savingRule, setSavingRule] = useState(false);
  const [ruleError, setRuleError] = useState<string | null>(null);
  const [ruleSuccess, setRuleSuccess] = useState<string | null>(null);

  // Estados de carga y error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inspector en seco
  const [selectedAgent, setSelectedAgent] = useState("Consultora");
  const [selectedSlug, setSelectedSlug] = useState("fhc");
  const [targetUrl, setTargetUrl] = useState("https://www.funerariahogardecristo.com/");
  const [assemblyResult, setAssemblyResult] =
    useState<PromptAssemblyPayload | null>(null);
  const [assembling, setAssembling] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);

  const loadFeedbackData = async (
    estado?: string,
    nivel?: string,
    clientSlug?: string,
    search?: string
  ) => {
    try {
      const res = await fetchVaultFeedback({
        limit: 100,
        estado: estado === "ALL" ? undefined : estado,
        nivel: nivel === "ALL" ? undefined : nivel,
        client_slug: clientSlug === "ALL" ? undefined : clientSlug,
        search: search?.trim() ? search.trim() : undefined,
      });
      setFeedback(res.data);
      if (res.summary) setFeedbackSummary(res.summary);
    } catch (err) {
      console.error("Error al cargar feedback:", err);
    }
  };

  const loadRulesCatalog = async () => {
    try {
      const res = await fetchStructuredRules();
      setStructuredRules(res.data);
    } catch (err) {
      console.error("Error al cargar catálogo de reglas:", err);
    }
  };

  // Carga inicial
  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      fetchVaultAgents(),
      fetchVaultTelemetry(telemDays),
      fetchVaultRules(),
      fetchVaultFeedback({ limit: 100 }),
      fetchStructuredRules(),
    ])
      .then(([agentsRes, telemRes, rulesRes, feedbackRes, catalogRes]) => {
        setAgents(agentsRes.data);
        setTelemetry(telemRes);
        setRules(rulesRes);
        setFeedback(feedbackRes.data);
        if (feedbackRes.summary) setFeedbackSummary(feedbackRes.summary);
        setStructuredRules(catalogRes.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, []);

  // Recarga al cambiar filtros de curación
  const handleFilterChange = (newStatus: string, newLevel: string, newClient: string, newSearch: string) => {
    setStatusFilter(newStatus);
    setLevelFilter(newLevel);
    setClientFilter(newClient);
    setSearchQuery(newSearch);
    void loadFeedbackData(newStatus, newLevel, newClient, newSearch);
  };

  const handleQuickTriage = async (
    feedbackId: string,
    newState: "SIN_CAMBIO_PROPUESTO" | "RECHAZADO" | "APROBADO" | "A_PRUEBA"
  ) => {
    setActionLoadingId(feedbackId);
    try {
      await triageFeedback(feedbackId, { estado_auditoria: newState });
      setFeedback((prev) =>
        prev.map((f) =>
          f.feedback_id === feedbackId ? { ...f, estado_auditoria: newState } : f
        )
      );
      void loadFeedbackData(statusFilter, levelFilter, clientFilter, searchQuery);
    } catch (err) {
      alert("Error al actualizar estado: " + String(err));
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenPromoteModal = (f: HitlFeedbackRow) => {
    setSelectedFeedbackForPromotion(f);
    const cleanClient = (f.client_slug || "GEN").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    const domainPrefix = (f.dominio || (f.nivel_auditoria?.includes("SEO") ? "SEO" : "PAID")).toUpperCase();
    const randomSuffix = Math.floor(10 + Math.random() * 90);
    const suggestedId = `${domainPrefix}_${cleanClient}_${randomSuffix}`;

    setRuleId(suggestedId);
    setRuleDomain(domainPrefix.includes("SEO") ? "SEO" : domainPrefix.includes("PAID") || domainPrefix.includes("MKT") ? "MKT" : "TRANSVERSAL");
    setRuleLevel(f.nivel_auditoria || "Per-URL SEO");
    setRuleTitle(f.regla_correctiva || f.comentario_humano || `Regla derivada de calibración ${f.client_slug}`);
    setRulePrecondition(`Hallazgo detectado en ${f.nivel_auditoria} para ${f.client_slug}`);
    setRuleDiagnosis(f.insight_generado?.slice(0, 300) || "");
    setRuleAction(f.regla_correctiva || f.comentario_humano || "");
    setRuleNoAplicaSi("No aplica si ");
    setRuleClient(f.client_slug || "ALL");
    setRuleStatus("ACTIVA");
    setRuleError(null);
    setRuleSuccess(null);
    setPromoteModalOpen(true);
  };

  const handleSaveRule = async () => {
    if (!ruleNoAplicaSi.trim() || ruleNoAplicaSi.trim() === "No aplica si") {
      setRuleError("La cláusula 'No aplica si' es obligatoria según la especificación normativa (1_STATUS.md §125).");
      return;
    }
    setSavingRule(true);
    setRuleError(null);
    try {
      await promoteRule({
        id_regla: ruleId.trim(),
        dominio: ruleDomain,
        nivel_auditoria: ruleLevel,
        titulo: ruleTitle.trim(),
        precondicion: rulePrecondition.trim(),
        diagnostico: ruleDiagnosis.trim(),
        accion: ruleAction.trim(),
        no_aplica_si: ruleNoAplicaSi.trim(),
        client_slug: ruleClient,
        estado: ruleStatus,
        origen_feedback_id: selectedFeedbackForPromotion?.id_recomendacion || selectedFeedbackForPromotion?.feedback_id,
      });
      setRuleSuccess("¡Regla formalizada y promovida a dim_gertrudis_regla exitosamente!");
      setTimeout(() => {
        setPromoteModalOpen(false);
        setRuleSuccess(null);
        void loadFeedbackData(statusFilter, levelFilter, clientFilter, searchQuery);
        void loadRulesCatalog();
      }, 1200);
    } catch (err) {
      setRuleError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingRule(false);
    }
  };

  const toggleInsight = (id: string) => {
    setExpandedInsights((prev) => ({ ...prev, [id]: !prev[id] }));
  };


  // Recarga al cambiar días de telemetría
  useEffect(() => {
    void fetchVaultTelemetry(telemDays).then(setTelemetry);
  }, [telemDays]);

  // Ejecutar ensamble en seco al cambiar selección o disparar
  const handleAssemble = async () => {
    setAssembling(true);
    try {
      const res = await assemblePromptDryRun(
        selectedAgent,
        selectedSlug,
        targetUrl
      );
      setAssemblyResult(res);
    } catch (e) {
      console.error("Error ensamble:", e);
    } finally {
      setAssembling(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    setCopiedSection(label);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const complianceIssues = useMemo(() => {
    return agents.filter((a) => a.compliance_status !== "OK").length;
  }, [agents]);

  // Estados para Cuotas de Proveedores (Gemini / Claude)
  const [quotaDays, setQuotaDays] = useState<1 | 7>(1);
  const [quotaRows, setQuotaRows] = useState<AiConsumptionRow[]>([]);
  const [quotaConfig, setQuotaConfig] = useState<AiQuotaConfig | null>(null);
  const [quotaPlan, setQuotaPlan] = useState("Pago por uso");
  const [quotaLoading, setQuotaLoading] = useState(false);

  useEffect(() => {
    if (activeTab === "quotas" && !quotaConfig) {
      setQuotaLoading(true);
      Promise.all([fetchAiQuotaConfig(), fetchAiConsumption(quotaDays)])
        .then(([cfg, rows]) => {
          setQuotaConfig(cfg);
          setQuotaRows(rows);
          if (cfg?.geminiPlans) {
            setQuotaPlan(
              Object.keys(cfg.geminiPlans).includes("Pago por uso")
                ? "Pago por uso"
                : Object.keys(cfg.geminiPlans)[0]
            );
          }
        })
        .finally(() => setQuotaLoading(false));
    }
  }, [activeTab, quotaConfig, quotaDays]);

  useEffect(() => {
    if (activeTab === "quotas" && quotaConfig) {
      setQuotaLoading(true);
      void fetchAiConsumption(quotaDays).then(setQuotaRows).finally(() => setQuotaLoading(false));
    }
  }, [quotaDays]);

  const quotaRequests = quotaRows.reduce((sum, row) => sum + numeric(row.peticiones_hoy), 0);
  const quotaTokensMin = quotaRows.reduce((sum, row) => sum + numeric(row.tokens_ultimo_min), 0);
  const quotaSelectedGemini = quotaConfig?.geminiPlans[quotaPlan] ?? {};
  const quotaPercentages = useMemo(() => {
    if (!quotaConfig) return [];
    return [
      ...Object.entries(quotaSelectedGemini),
      ...Object.entries(quotaConfig.claude ?? {}),
    ]
      .flatMap(([model, limits]) => {
        const row = findRow(quotaRows, model);
        return [
          [numeric(row?.peticiones_ultimo_min), limits.RPM],
          [numeric(row?.tokens_ultimo_min), limits.TPM],
          [numeric(row?.peticiones_hoy), limits.RPD],
        ] as Array<[number, number | null]>;
      })
      .filter(([, limit]) => Boolean(limit))
      .map(([value, limit]) => (value / Number(limit)) * 100);
  }, [quotaSelectedGemini, quotaConfig, quotaRows]);

  const quotaAverage =
    quotaPercentages.length ? quotaPercentages.reduce((s, v) => s + v, 0) / quotaPercentages.length : 0;
  const siriusStatus = quotaAverage > 90 ? "SATURADO" : quotaAverage > 70 ? "RIESGO" : "OK";

  if (loading) return <LoadingState />;

  if (error) {
    return (
      <div className="page">
        <section className="error-state">
          <ShieldAlert size={28} />
          <h1>Error cargando Cerebro Gertrudis</h1>
          <p>{error}</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page page-vault">
      {/* Header */}
      <section className="page-intro enter">
        <div>
          <span className="eyebrow">Inteligencia Aumentada · Wolf Datalake</span>
          <h1>Gertrudis · Cerebro & Observabilidad</h1>
          <p>
            Gestión de sombreros, observabilidad agnóstica de tokens y costos por modelo,
            cuotas oficiales de proveedores y calibración HITL.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="segmented-control" aria-label="Secciones de Gertrudis">
          <button
            className={activeTab === "control" ? "is-active" : ""}
            onClick={() => handleTabChange("control")}
          >
            Control & Sombreros
          </button>
          <button
            className={activeTab === "telemetry" ? "is-active" : ""}
            onClick={() => handleTabChange("telemetry")}
          >
            Telemetría Tokens
          </button>
          <button
            className={activeTab === "quotas" ? "is-active" : ""}
            onClick={() => handleTabChange("quotas")}
          >
            Cuotas Proveedores
          </button>
          <button
            className={activeTab === "rules" ? "is-active" : ""}
            onClick={() => handleTabChange("rules")}
          >
            Reglas del Vault
          </button>
          <button
            className={activeTab === "assembly" ? "is-active" : ""}
            onClick={() => handleTabChange("assembly")}
          >
            Inspector en Seco
          </button>
          <button
            className={activeTab === "feedback" ? "is-active" : ""}
            onClick={() => handleTabChange("feedback")}
          >
            Calibración HITL
          </button>
        </div>
      </section>

      {/* KPIs Superiores */}
      <section className="metric-grid">
        <MetricCard
          label="Sombreros Activos"
          value={String(agents.filter((a) => a.is_active).length)}
          note="en dim_gertrudis_agente"
          icon={BrainCircuit}
          tone="accent"
        />
        <MetricCard
          label="Cumplimiento Regla §7"
          value={complianceIssues === 0 ? "100% OK" : `${complianceIssues} Alertas`}
          note={complianceIssues === 0 ? "Flash vs Pro balanceado" : "Revisar asignación de tiers"}
          icon={complianceIssues === 0 ? ShieldCheck : AlertTriangle}
          tone={complianceIssues === 0 ? "good" : "warning"}
        />
        <MetricCard
          label="Tokens Periodo"
          value={compactNumber.format(telemetry?.total_tokens ?? 0)}
          note={`últimos ${telemDays} días`}
          icon={Gauge}
        />
        <MetricCard
          label="Costo Estimado"
          value={`$${(telemetry?.total_cost_usd ?? 0).toFixed(2)} USD`}
          note={`${telemetry?.total_requests ?? 0} peticiones`}
          icon={Coins}
          tone="accent"
        />
      </section>

      {/* VISTA 1: CONTROL DE GESTIÓN & TIERS (§7) */}
      {activeTab === "control" && (
        <div className="vault-tab-content enter">
          {/* TABLA UNIFICADA DE GOBERNANZA Y CRITERIO CANÓNICO */}
          <section className="panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow orange">Gobernanza Canónica y Asignación de Modelos</span>
                <h2>Matriz de Sombreros Gertrudis (AI_PROMPT_VAULT.md §7)</h2>
              </div>
            </div>
            <div className="table-scroll">
              <table className="usage-table vault-table">
                <thead>
                  <tr>
                    <th style={{ width: "18%" }}>Sombrero</th>
                    <th style={{ width: "30%" }}>Tarea Operativa</th>
                    <th style={{ width: "18%" }}>Modelo & Fallback</th>
                    <th style={{ width: "34%" }}>Criterio Canónico (Por qué)</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((ag) => (
                    <tr key={ag.agente}>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                          <strong>{ag.agente}</strong>
                          <div>
                            {ag.compliance_status === "OK" ? (
                              <span className="vault-status-pill is-ok" style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}>
                                <ShieldCheck size={11} /> Conforme §7
                              </span>
                            ) : ag.compliance_status === "VIOLACION_COSTO" ? (
                              <span className="vault-status-pill is-danger" style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}>
                                <AlertTriangle size={11} /> Violación costo
                              </span>
                            ) : (
                              <span className="vault-status-pill is-warning" style={{ fontSize: "0.75rem", padding: "0.15rem 0.5rem" }}>
                                <AlertTriangle size={11} /> Riesgo calidad
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: "0.9rem", color: "var(--text-main, #f1f5f9)", lineHeight: 1.45 }}>
                        {ag.tarea}
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                          <span
                            className={`vault-tier-badge ${
                              ag.modelo_live.includes("Flash") ? "is-flash" : "is-pro"
                            }`}
                            style={{ alignSelf: "flex-start" }}
                          >
                            <Check size={12} /> {ag.modelo_live}
                          </span>
                          <span
                            className="mono"
                            style={{ fontSize: "0.75rem", color: "var(--muted, #94a3b8)", whiteSpace: "nowrap" }}
                            title={`Cadena de fallback: ${ag.fallback_formatted}`}
                          >
                            ↳ {ag.fallback_formatted}
                          </span>
                        </div>
                      </td>
                      <td style={{ fontSize: "0.88rem", color: "var(--text-dim, #cbd5e1)", lineHeight: 1.5 }}>
                        {ag.por_que}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {/* VISTA 2: TELEMETRÍA DE TOKENS & COSTOS */}
      {activeTab === "telemetry" && (
        <div className="vault-tab-content enter">
          <section className="panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow blue">Consumo Real (sys_logs.api_consumption_raw)</span>
                <h2>Desglose de Tokens y Costos por Sombrero</h2>
              </div>
              <div className="segmented-control" aria-label="Ventana de tiempo">
                <button
                  className={telemDays === 7 ? "is-active" : ""}
                  onClick={() => setTelemDays(7)}
                >
                  7 días
                </button>
                <button
                  className={telemDays === 30 ? "is-active" : ""}
                  onClick={() => setTelemDays(30)}
                >
                  30 días
                </button>
                <button
                  className={telemDays === 90 ? "is-active" : ""}
                  onClick={() => setTelemDays(90)}
                >
                  90 días
                </button>
              </div>
            </div>

            <div className="table-scroll">
              <table className="usage-table">
                <thead>
                  <tr>
                    <th>Sombrero</th>
                    <th>Proveedor</th>
                    <th>Modelo Ejecutado</th>
                    <th>Peticiones</th>
                    <th>Tokens Entrada</th>
                    <th>Tokens Salida</th>
                    <th>Tokens Totales</th>
                    <th>Costo Est. (USD)</th>
                    <th>Última llamada</th>
                  </tr>
                </thead>
                <tbody>
                  {(telemetry?.rows ?? []).map((row, idx) => {
                    const prov = getProviderInfo(row.modelo);
                    return (
                      <tr key={`${row.sombrero}-${row.modelo}-${idx}`}>
                        <td>
                          <strong>{row.sombrero}</strong>
                        </td>
                        <td>
                          <span className={`provider-badge ${prov.className}`}>
                            {prov.name}
                          </span>
                        </td>
                        <td className="mono" style={{ fontSize: "0.82rem" }}>
                          {row.modelo}
                        </td>
                        <td>{row.peticiones.toLocaleString("es-CL")}</td>
                        <td>{compactNumber.format(row.input_tokens)}</td>
                        <td>{compactNumber.format(row.output_tokens)}</td>
                        <td>
                          <strong>{compactNumber.format(row.total_tokens)}</strong>
                        </td>
                        <td style={{ color: "var(--teal, #4fd1c5)" }}>
                          ${row.costo_estimado_usd.toFixed(4)}
                        </td>
                        <td style={{ fontSize: "0.8rem", color: "var(--muted, #94a3b8)" }}>
                          {row.ultimo_timestamp
                            ? new Date(row.ultimo_timestamp).toLocaleString("es-CL")
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {(!telemetry?.rows || telemetry.rows.length === 0) && (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "2rem" }}>
                        Sin llamadas registradas en la ventana de {telemDays} días.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {/* VISTA 3: CUOTAS OFICIALES & MONITOR DE PROVEEDORES */}
      {activeTab === "quotas" && (
        <div className="vault-tab-content enter">
          <section className="page-intro" style={{ marginBottom: "1rem" }}>
            <div>
              <span className="eyebrow">Observabilidad · Límites de API</span>
              <h2>Cuotas Oficiales por Modelo</h2>
              <p>
                Monitoreo en vivo de RPM, TPM y RPD configurados en <code>audit_ai_consumption.py</code> con buffer de seguridad del 80%.
              </p>
            </div>
            <div className="segmented-control" aria-label="Ventana de consumo">
              <button
                className={quotaDays === 1 ? "is-active" : ""}
                onClick={() => setQuotaDays(1)}
              >
                Hoy
              </button>
              <button
                className={quotaDays === 7 ? "is-active" : ""}
                onClick={() => setQuotaDays(7)}
              >
                7 días
              </button>
            </div>
          </section>

          {quotaLoading && <LoadingState />}

          {!quotaLoading && (
            <>
              <section className="metric-grid" style={{ marginBottom: "1.5rem" }}>
                <MetricCard
                  label="Proveedor Activo"
                  value={quotaConfig?.activeProvider ?? "GEMINI"}
                  note={quotaPlan}
                  icon={Bot}
                  tone="accent"
                />
                <MetricCard
                  label="Peticiones Periodo"
                  value={compactNumber.format(quotaRequests)}
                  note="en la ventana"
                  icon={Zap}
                />
                <MetricCard
                  label="Tokens / min"
                  value={compactNumber.format(quotaTokensMin)}
                  note="TPM último min"
                  icon={Gauge}
                />
                <MetricCard
                  label="Estado Sirius"
                  value={siriusStatus}
                  note={`${quotaAverage.toFixed(1)}% uso medio`}
                  icon={Sparkles}
                  tone={siriusStatus === "OK" ? "good" : siriusStatus === "RIESGO" ? "warning" : "danger"}
                />
              </section>

              <section className="panel quota-panel" style={{ marginBottom: "1.5rem" }}>
                <div className="panel-heading">
                  <div>
                    <span className="eyebrow blue">Google Gemini</span>
                    <h2>Cuotas por Modelo</h2>
                  </div>
                  <label className="quota-plan">
                    Plan de facturación
                    <select value={quotaPlan} onChange={(e) => setQuotaPlan(e.target.value)}>
                      {Object.keys(quotaConfig?.geminiPlans ?? {}).map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <div className="quota-grid">
                  {Object.entries(quotaSelectedGemini).map(([model, limits]) => (
                    <ModelQuota key={model} model={model} limits={limits} rows={quotaRows} />
                  ))}
                </div>
              </section>

              <section className="panel quota-panel" style={{ marginBottom: "1.5rem" }}>
                <div className="panel-heading">
                  <div>
                    <span className="eyebrow orange">Anthropic Claude</span>
                    <h2>Cuotas por Modelo</h2>
                  </div>
                </div>
                <div className="quota-grid">
                  {Object.entries(quotaConfig?.claude ?? {}).map(([model, limits]) => (
                    <ModelQuota key={model} model={model} limits={limits} rows={quotaRows} />
                  ))}
                </div>
              </section>

              <section className="panel model-panel">
                <div className="panel-heading">
                  <div>
                    <span className="eyebrow blue">Consumo Crudo</span>
                    <h2>Peticiones y Tokens por Modelo en el Periodo</h2>
                  </div>
                </div>
                <div className="table-scroll">
                  <table className="usage-table">
                    <thead>
                      <tr>
                        <th>Modelo</th>
                        <th>Proveedor</th>
                        <th>RPM (último min)</th>
                        <th>TPM (último min)</th>
                        <th>RPD (peticiones)</th>
                        <th>Última petición</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotaRows.map((r) => {
                        const prov = getProviderInfo(String(r.modelo));
                        return (
                          <tr key={String(r.modelo)}>
                            <td className="mono" style={{ fontSize: "0.82rem" }}>
                              {String(r.modelo)}
                            </td>
                            <td>
                              <span className={`provider-badge ${prov.className}`}>
                                {prov.name}
                              </span>
                            </td>
                            <td>{numeric(r.peticiones_ultimo_min)}</td>
                            <td>{compactNumber.format(numeric(r.tokens_ultimo_min))}</td>
                            <td>{compactNumber.format(numeric(r.peticiones_hoy))}</td>
                            <td>
                              {r.ultima_peticion
                                ? new Date(r.ultima_peticion).toLocaleString("es-CL")
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                      {quotaRows.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center", padding: "2rem" }}>
                            Sin peticiones en el periodo seleccionado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      )}

      {/* VISTA 3: REGLAS DEL VAULT (AI_PROMPT_VAULT.md) */}
      {activeTab === "rules" && (
        <div className="vault-tab-content enter">
          <section className="panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow blue">ai_context/AI_PROMPT_VAULT.md</span>
                <h2>Corpus Normativo y Prompts Maestros</h2>
              </div>
              <button
                className="button button-secondary"
                type="button"
                onClick={() =>
                  copyToClipboard(rules?.full_content ?? "", "full_vault")
                }
              >
                {copiedSection === "full_vault" ? (
                  <>
                    <CheckCheck size={14} /> Copiado
                  </>
                ) : (
                  <>
                    <Copy size={14} /> Copiar Vault Completo
                  </>
                )}
              </button>
            </div>

            <div className="vault-rules-accordion">
              {(rules?.sections ?? []).map((sec, i) => (
                <details className="vault-rule-block" key={i} open={i === 0}>
                  <summary>
                    <span className="rule-title">{sec.title}</span>
                    <button
                      className="button-icon-copy"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(sec.content, `sec_${i}`);
                      }}
                      title="Copiar sección"
                    >
                      {copiedSection === `sec_${i}` ? (
                        <CheckCheck size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </summary>
                  <div className="vault-rule-body">
                    <pre>
                      <code>{sec.content}</code>
                    </pre>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* VISTA 4: INSPECTOR EN SECO (DRY-RUN ASSEMBLY) */}
      {activeTab === "assembly" && (
        <div className="vault-tab-content enter">
          <section className="panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow blue">Dry-Run sin consumo de tokens</span>
                <h2>Inspector de Ensamble de Prompt</h2>
              </div>
              <p style={{ color: "var(--muted, #94a3b8)", fontSize: "0.88rem" }}>
                Previsualiza con datos vivos cómo se arman el System Prompt y el
                User Message con aislamiento XML, sin disparar ninguna llamada al LLM.
              </p>
            </div>

            {/* Selector de Controles */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <label className="filter-label">
                Sombrero de Gertrudis
                <select
                  value={selectedAgent}
                  onChange={(e) => setSelectedAgent(e.target.value)}
                >
                  <option value="Consultora">Consultora (Paid Media)</option>
                  <option value="Consultora_SEO">Consultora (SEO)</option>
                  <option value="Analista">Analista (Reporte Multicanal)</option>
                  <option value="Sociologa">Socióloga (Escucha Social)</option>
                  <option value="Wolf Notebook">Wolf Notebook (RAG)</option>
                </select>
              </label>

              <label className="filter-label">
                Cliente de la Cartera
                <select
                  value={selectedSlug}
                  onChange={(e) => setSelectedSlug(e.target.value)}
                >
                  {clients.map((c) => (
                    <option key={c.clientSlug} value={c.clientSlug}>
                      {c.displayName} ({c.clientSlug})
                    </option>
                  ))}
                </select>
              </label>

              {selectedAgent === "Consultora_SEO" && (
                <label className="filter-label">
                  URL Objetivo (SEO)
                  <input
                    type="text"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </label>
              )}

              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <button
                  className="button button-primary"
                  type="button"
                  disabled={assembling}
                  onClick={handleAssemble}
                  style={{ width: "100%" }}
                >
                  {assembling ? (
                    <>
                      <RefreshCw size={15} className="spin" /> Ensamblando...
                    </>
                  ) : (
                    <>
                      <Eye size={15} /> Inspeccionar Ensamble
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Resultado del Ensamble */}
            {assemblyResult && (
              <div className="assembly-result">
                {/* Barra de Diagnóstico de Ensamble */}
                <div className="assembly-meta-bar">
                  <div>
                    <span>Objetivo de Negocio:</span>{" "}
                    <strong>
                      {assemblyResult.objective_declared
                        ? `🟢 ${assemblyResult.objective_label}`
                        : "🔴 Sin Declarar"}
                    </strong>
                  </div>
                  <div>
                    <span>Tokens Estimados:</span>{" "}
                    <strong>~{assemblyResult.estimated_tokens.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span>Costo de Llamada:</span>{" "}
                    <strong style={{ color: "var(--teal, #4fd1c5)" }}>$0.00 (Dry-Run)</strong>
                  </div>
                </div>

                {/* Split View */}
                <div className="assembly-split-grid">
                  <div className="assembly-pane">
                    <div className="pane-header">
                      <span>System Prompt (Instrucción de Rol y Reglas)</span>
                      <button
                        className="button-icon-copy"
                        type="button"
                        onClick={() =>
                          copyToClipboard(
                            assemblyResult.system_prompt,
                            "sys_prompt"
                          )
                        }
                      >
                        {copiedSection === "sys_prompt" ? (
                          <CheckCheck size={14} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </div>
                    <pre>
                      <code>{assemblyResult.system_prompt}</code>
                    </pre>
                  </div>

                  <div className="assembly-pane">
                    <div className="pane-header">
                      <span>User Message (Datos Crudos & Objetivo Inyectado)</span>
                      <button
                        className="button-icon-copy"
                        type="button"
                        onClick={() =>
                          copyToClipboard(
                            assemblyResult.user_message,
                            "user_msg"
                          )
                        }
                      >
                        {copiedSection === "user_msg" ? (
                          <CheckCheck size={14} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </div>
                    <pre>
                      <code>{assemblyResult.user_message}</code>
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* VISTA 5: CALIBRACIÓN HITL (sys_ai_feedback) & CATÁLOGO DE REGLAS */}
      {activeTab === "feedback" && (
        <div className="vault-tab-content enter">
          {/* Header & Métricas de Curación */}
          <div className="curation-kpis-grid">
            <div className={`curation-kpi-card ${statusFilter === "ALL" ? "is-active" : ""}`}>
              <span className="curation-kpi-label">
                <Layers size={13} /> Evaluaciones Totales
              </span>
              <strong className="curation-kpi-value">
                {compactNumber.format(feedbackSummary?.total ?? feedback.length)}
              </strong>
            </div>

            <div className={`curation-kpi-card tone-warning ${statusFilter === "PENDIENTE_REVISION" ? "is-active" : ""}`}>
              <span className="curation-kpi-label">
                <Clock3 size={13} /> Pendientes de Triage
              </span>
              <strong className="curation-kpi-value" style={{ color: "#fbbf24" }}>
                {compactNumber.format(feedbackSummary?.pendientes ?? 0)}
              </strong>
            </div>

            <div className={`curation-kpi-card tone-ok ${statusFilter === "APROBADO" ? "is-active" : ""}`}>
              <span className="curation-kpi-label">
                <ShieldCheck size={13} /> Promovidas al Vault
              </span>
              <strong className="curation-kpi-value" style={{ color: "#4ade80" }}>
                {compactNumber.format(feedbackSummary?.aprobados ?? 0)}
              </strong>
            </div>

            <div className={`curation-kpi-card tone-info ${statusFilter === "SIN_CAMBIO_PROPUESTO" ? "is-active" : ""}`}>
              <span className="curation-kpi-label">
                <CheckCircle2 size={13} /> Sin Cambio Requerido
              </span>
              <strong className="curation-kpi-value" style={{ color: "#38bdf8" }}>
                {compactNumber.format(feedbackSummary?.sin_cambio ?? 0)}
              </strong>
            </div>

            <div className={`curation-kpi-card ${statusFilter === "A_PRUEBA" ? "is-active" : ""}`}>
              <span className="curation-kpi-label">
                <AlertTriangle size={13} /> En Calibración / A Prueba
              </span>
              <strong className="curation-kpi-value">
                {compactNumber.format(feedbackSummary?.a_prueba ?? 0)}
              </strong>
            </div>
          </div>

          {/* Subtabs de navegación: Bandeja vs Catálogo */}
          <div className="curation-subtabs">
            <button
              type="button"
              className={`curation-subtab-btn ${curationView === "inbox" ? "is-active" : ""}`}
              onClick={() => setCurationView("inbox")}
            >
              <Edit3 size={15} /> Bandeja de Curación HITL ({feedback.length})
            </button>
            <button
              type="button"
              className={`curation-subtab-btn ${curationView === "catalog" ? "is-active" : ""}`}
              onClick={() => {
                setCurationView("catalog");
                void loadRulesCatalog();
              }}
            >
              <BookOpen size={15} /> Catálogo de Reglas Formalizadas ({structuredRules.length})
            </button>
          </div>

          {/* BANDEJA DE CURACIÓN HITL */}
          {curationView === "inbox" && (
            <section className="panel">
              {/* Barra de Filtros y Búsqueda */}
              <div className="curation-toolbar">
                <div className="curation-status-filters">
                  <button
                    type="button"
                    className={`curation-filter-pill ${statusFilter === "ALL" ? "is-active" : ""}`}
                    onClick={() => handleFilterChange("ALL", levelFilter, clientFilter, searchQuery)}
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    className={`curation-filter-pill ${statusFilter === "PENDIENTE_REVISION" ? "is-active pill-pending" : ""}`}
                    onClick={() => handleFilterChange("PENDIENTE_REVISION", levelFilter, clientFilter, searchQuery)}
                  >
                    Pendientes ({feedbackSummary?.pendientes ?? 0})
                  </button>
                  <button
                    type="button"
                    className={`curation-filter-pill ${statusFilter === "APROBADO" ? "is-active pill-ok" : ""}`}
                    onClick={() => handleFilterChange("APROBADO", levelFilter, clientFilter, searchQuery)}
                  >
                    Aprobados / Con Regla ({feedbackSummary?.aprobados ?? 0})
                  </button>
                  <button
                    type="button"
                    className={`curation-filter-pill ${statusFilter === "SIN_CAMBIO_PROPUESTO" ? "is-active pill-info" : ""}`}
                    onClick={() => handleFilterChange("SIN_CAMBIO_PROPUESTO", levelFilter, clientFilter, searchQuery)}
                  >
                    Sin Cambio ({feedbackSummary?.sin_cambio ?? 0})
                  </button>
                  <button
                    type="button"
                    className={`curation-filter-pill ${statusFilter === "A_PRUEBA" ? "is-active" : ""}`}
                    onClick={() => handleFilterChange("A_PRUEBA", levelFilter, clientFilter, searchQuery)}
                  >
                    A Prueba ({feedbackSummary?.a_prueba ?? 0})
                  </button>
                  <button
                    type="button"
                    className={`curation-filter-pill ${statusFilter === "RECHAZADO" ? "is-active" : ""}`}
                    onClick={() => handleFilterChange("RECHAZADO", levelFilter, clientFilter, searchQuery)}
                  >
                    Rechazados ({feedbackSummary?.rechazados ?? 0})
                  </button>
                </div>

                <div className="curation-search-group">
                  <div className="filter-label">
                    <select
                      value={levelFilter}
                      onChange={(e) => handleFilterChange(statusFilter, e.target.value, clientFilter, searchQuery)}
                    >
                      <option value="ALL">Todos los Niveles</option>
                      <option value="Per-URL SEO">Per-URL SEO</option>
                      <option value="Per-campaña Paid">Per-campaña Paid</option>
                      <option value="Reporte de cuenta">Reporte de cuenta</option>
                      <option value="Per-cliente Social">Per-cliente Social</option>
                      <option value="Torre de Control">Torre de Control</option>
                    </select>
                  </div>

                  <div className="filter-label">
                    <select
                      value={clientFilter}
                      onChange={(e) => handleFilterChange(statusFilter, levelFilter, e.target.value, searchQuery)}
                    >
                      <option value="ALL">Todos los Clientes</option>
                      {clients.map((c) => (
                        <option key={c.clientSlug} value={c.clientSlug}>
                          {c.displayName} ({c.clientSlug})
                        </option>
                      ))}
                    </select>

                  </div>

                  <div className="curation-search-input">
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Buscar en feedback..."
                      value={searchQuery}
                      onChange={(e) => handleFilterChange(statusFilter, levelFilter, clientFilter, e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Lista de Tarjetas de Curación */}
              <div className="curation-cards-list">
                {feedback.map((f) => {
                  const stateClass =
                    f.estado_auditoria === "PENDIENTE_REVISION"
                      ? "status-pending"
                      : f.estado_auditoria === "APROBADO"
                      ? "status-ok"
                      : f.estado_auditoria === "SIN_CAMBIO_PROPUESTO"
                      ? "status-info"
                      : "status-danger";

                  const califClass =
                    f.calificacion === "APROBADO" || f.calificacion === "POSITIVO"
                      ? "calif-aprobado"
                      : f.calificacion === "A_PRUEBA"
                      ? "calif-aprueba"
                      : "calif-rechazado";

                  const isExpanded = expandedInsights[f.feedback_id];
                  const insightText = f.insight_generado || "";
                  const shouldTruncate = insightText.length > 220;

                  return (
                    <article key={f.feedback_id} className={`curation-card ${stateClass}`}>
                      <div className="curation-card-header">
                        <div className="curation-card-meta">
                          <strong style={{ fontSize: "0.95rem", color: "#fff" }}>
                            {f.client_slug}
                          </strong>
                          <span className="vault-tier-badge is-pro">
                            {f.nivel_auditoria}
                          </span>
                          {f.dominio && (
                            <span style={{ fontSize: "0.75rem", color: "var(--muted, #94a3b8)" }}>
                              [{f.dominio}]
                            </span>
                          )}
                          {f.elemento_auditado && (
                            <span style={{ fontSize: "0.72rem", color: "#a5b4fc" }}>
                              {f.elemento_auditado}
                            </span>
                          )}
                        </div>

                        <div className="curation-card-badges">
                          <span className={`curation-badge ${califClass}`}>
                            {f.calificacion || "SIN CALIFICAR"}
                          </span>
                          <span
                            className={`vault-status-pill ${
                              f.estado_auditoria === "APROBADO"
                                ? "is-ok"
                                : f.estado_auditoria === "SIN_CAMBIO_PROPUESTO"
                                ? "is-ok"
                                : f.estado_auditoria === "PENDIENTE_REVISION"
                                ? "is-warning"
                                : "is-danger"
                            }`}
                          >
                            {f.estado_auditoria.replace(/_/g, " ")}
                          </span>
                        </div>
                      </div>

                      <div className="curation-card-content">
                        {/* Insight generado por Gertrudis */}
                        {insightText && (
                          <div className="curation-insight-box">
                            <span style={{ fontSize: "0.68rem", textTransform: "uppercase", color: "var(--muted, #94a3b8)", display: "block", marginBottom: "4px" }}>
                              Recomendación / Diagnóstico IA
                            </span>
                            <p style={{ margin: 0 }}>
                              {shouldTruncate && !isExpanded
                                ? `${insightText.slice(0, 220)}...`
                                : insightText}
                            </p>
                            {shouldTruncate && (
                              <button
                                type="button"
                                style={{ background: "none", border: "none", color: "var(--teal, #4fd1c5)", cursor: "pointer", fontSize: "0.72rem", padding: "4px 0", display: "inline-flex", alignItems: "center", gap: "4px" }}
                                onClick={() => toggleInsight(f.feedback_id)}
                              >
                                {isExpanded ? (
                                  <>Menos detalle <ChevronUp size={12} /></>
                                ) : (
                                  <>Ver recomendación completa <ChevronDown size={12} /></>
                                )}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Comentario Humano de Calibración */}
                        <div className="curation-comment-box">
                          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px", fontSize: "0.68rem", textTransform: "uppercase" }}>
                            <strong style={{ color: "#38bdf8" }}>Juicio del Auditor Senior</strong>
                            <span style={{ color: "var(--muted, #94a3b8)" }}>
                              {f.user_email ? f.user_email.split("@")[0] : "senior"} · {f.fecha_feedback ? new Date(f.fecha_feedback).toLocaleDateString("es-CL") : ""}
                            </span>
                          </div>
                          <p style={{ margin: 0 }}>{f.comentario_humano || "Sin comentario registrado."}</p>
                        </div>

                        {/* Comentario IA si existe */}
                        {f.comentario_ia && (
                          <div style={{ background: "rgba(129, 140, 248, 0.05)", border: "1px solid rgba(129, 140, 248, 0.2)", borderRadius: "8px", padding: "10px 12px", fontSize: "0.78rem", color: "#c7d2fe" }}>
                            <span style={{ fontSize: "0.66rem", textTransform: "uppercase", color: "#818cf8", display: "block", marginBottom: "2px" }}>
                              Postura IA Divergente
                            </span>
                            <p style={{ margin: 0 }}>{f.comentario_ia}</p>
                          </div>
                        )}

                        {/* Regla Correctiva Sugerida */}
                        {f.regla_correctiva && (
                          <div className="curation-rule-suggestion">
                            <Sparkles size={16} style={{ flexShrink: 0, marginTop: "2px" }} />
                            <div>
                              <strong style={{ display: "block", fontSize: "0.74rem", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                                Regla Correctiva Sugerida
                              </strong>
                              <span>{f.regla_correctiva}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Barra de Acciones de Curación */}
                      <div className="curation-actions-bar">
                        <button
                          type="button"
                          className="curation-btn btn-promote"
                          onClick={() => handleOpenPromoteModal(f)}
                          title="Formalizar regla estructurada y agregar al corpus normativo"
                        >
                          <Sparkles size={13} /> Promover a Regla
                        </button>

                        {f.estado_auditoria !== "SIN_CAMBIO_PROPUESTO" && (
                          <button
                            type="button"
                            className="curation-btn btn-no-change"
                            disabled={actionLoadingId === f.feedback_id}
                            onClick={() => handleQuickTriage(f.id_recomendacion || f.feedback_id, "SIN_CAMBIO_PROPUESTO")}
                            title="Marcar como evaluado y correcto sin necesidad de alterar el prompt"
                          >
                            <CheckCircle2 size={13} /> Aprobar Sin Cambio
                          </button>
                        )}

                        {f.estado_auditoria !== "RECHAZADO" && (
                          <button
                            type="button"
                            className="curation-btn btn-reject"
                            disabled={actionLoadingId === f.feedback_id}
                            onClick={() => handleQuickTriage(f.id_recomendacion || f.feedback_id, "RECHAZADO")}
                            title="Descartar o marcar como hallazgo rechazado"
                          >
                            <XCircle size={13} /> Rechazar
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}

                {feedback.length === 0 && (
                  <div style={{ textAlign: "center", padding: "3rem", background: "rgba(0,0,0,0.2)", borderRadius: "10px" }}>
                    <CheckCircle2 size={36} style={{ color: "var(--teal, #4fd1c5)", marginBottom: "10px" }} />
                    <h3 style={{ margin: "0 0 6px" }}>Bandeja al día</h3>
                    <p style={{ margin: 0, color: "var(--muted, #94a3b8)", fontSize: "0.85rem" }}>
                      No se encontraron evaluaciones con los filtros seleccionados.
                    </p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* CATÁLOGO DE REGLAS FORMALIZADAS (dim_gertrudis_regla) */}
          {curationView === "catalog" && (
            <section className="panel">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow green">Corpus Estructurado</span>
                  <h2>Reglas Normativas Vigentes (wolf_ai.dim_gertrudis_regla)</h2>
                </div>
                <button
                  type="button"
                  className="curation-btn btn-promote"
                  onClick={() => {
                    setSelectedFeedbackForPromotion(null);
                    setRuleId(`RULE_CUSTOM_${Math.floor(10 + Math.random() * 90)}`);
                    setRuleDomain("SEO");
                    setRuleLevel("Per-URL SEO");
                    setRuleTitle("");
                    setRulePrecondition("");
                    setRuleDiagnosis("");
                    setRuleAction("");
                    setRuleNoAplicaSi("No aplica si ");
                    setRuleClient("ALL");
                    setRuleStatus("ACTIVA");
                    setRuleError(null);
                    setRuleSuccess(null);
                    setPromoteModalOpen(true);
                  }}
                >
                  <Sparkles size={14} /> Crear Nueva Regla
                </button>
              </div>

              <div className="rules-catalog-grid">
                {structuredRules.map((r) => (
                  <article key={r.id_regla} className="rule-catalog-card">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                      <div>
                        <strong style={{ fontSize: "0.88rem", color: "var(--teal, #4fd1c5)", display: "block" }}>
                          {r.id_regla}
                        </strong>
                        <h4 style={{ margin: "2px 0 0", fontSize: "0.95rem" }}>{r.titulo}</h4>
                      </div>
                      <span className={`vault-status-pill ${r.estado === "ACTIVA" ? "is-ok" : "is-warning"}`}>
                        {r.estado}
                      </span>
                    </div>

                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", fontSize: "0.72rem" }}>
                      <span className="vault-tier-badge is-pro">{r.nivel_auditoria}</span>
                      <span style={{ padding: "2px 6px", background: "rgba(255,255,255,0.05)", borderRadius: "4px", color: "var(--muted, #94a3b8)" }}>
                        {r.dominio}
                      </span>
                      <span style={{ padding: "2px 6px", background: "rgba(255,255,255,0.05)", borderRadius: "4px", color: "#a5b4fc" }}>
                        Cliente: {r.client_slug}
                      </span>
                    </div>

                    <div style={{ fontSize: "0.8rem", color: "#cbd5e1", display: "grid", gap: "6px" }}>
                      <div>
                        <strong style={{ color: "var(--muted, #94a3b8)", fontSize: "0.72rem", textTransform: "uppercase" }}>Precondición:</strong>{" "}
                        {r.precondicion}
                      </div>
                      <div>
                        <strong style={{ color: "var(--muted, #94a3b8)", fontSize: "0.72rem", textTransform: "uppercase" }}>Acción:</strong>{" "}
                        {r.accion}
                      </div>
                    </div>

                    {/* Cláusula de guarda anti-falsos positivos */}
                    <div className="rule-card-guard">
                      <strong style={{ display: "block", textTransform: "uppercase", fontSize: "0.68rem", color: "#f87171" }}>
                        Cláusula de Guarda (No aplica si):
                      </strong>
                      <span>{r.no_aplica_si}</span>
                    </div>

                    <div style={{ fontSize: "0.7rem", color: "var(--dim, #64748b)", display: "flex", justifyContent: "space-between", marginTop: "4px" }}>
                      <span>Autor: {r.autor}</span>
                      <span>{r.updated_at ? new Date(r.updated_at).toLocaleDateString("es-CL") : ""}</span>
                    </div>
                  </article>
                ))}

                {structuredRules.length === 0 && (
                  <div style={{ gridColumn: "1 / -1", textAlign: "center", padding: "3rem" }}>
                    Sin reglas estructuradas registradas en dim_gertrudis_regla.
                  </div>
                )}
              </div>
            </section>
          )}

          {/* MODAL DE FORMALIZACIÓN Y PROMOCIÓN A REGLA */}
          {promoteModalOpen && (
            <div className="curation-modal-backdrop" onClick={() => !savingRule && setPromoteModalOpen(false)}>
              <div className="curation-modal-dialog" onClick={(e) => e.stopPropagation()}>
                <div className="curation-modal-header">
                  <h3>
                    <Sparkles size={18} style={{ color: "var(--teal, #4fd1c5)" }} /> Formalizar Regla en dim_gertrudis_regla
                  </h3>
                  <button
                    type="button"
                    style={{ background: "none", border: "none", color: "var(--muted, #94a3b8)", cursor: "pointer" }}
                    onClick={() => !savingRule && setPromoteModalOpen(false)}
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="curation-modal-body">
                  {ruleError && (
                    <div className="detail-notice is-danger">
                      <AlertTriangle size={16} />
                      <span>{ruleError}</span>
                    </div>
                  )}

                  {ruleSuccess && (
                    <div style={{ padding: "12px", background: "rgba(74, 222, 128, 0.15)", border: "1px solid #4ade80", borderRadius: "8px", color: "#4ade80", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "8px" }}>
                      <CheckCircle2 size={16} />
                      <span>{ruleSuccess}</span>
                    </div>
                  )}

                  <div className="curation-form-row">
                    <div className="curation-form-field">
                      <label>ID de Regla (Único)</label>
                      <input
                        type="text"
                        value={ruleId}
                        onChange={(e) => setRuleId(e.target.value.toUpperCase())}
                        placeholder="Ej. SEO_AFEX_CWV_01"
                      />
                    </div>
                    <div className="curation-form-field">
                      <label>Dominio</label>
                      <select value={ruleDomain} onChange={(e) => setRuleDomain(e.target.value)}>
                        <option value="SEO">SEO</option>
                        <option value="MKT">MKT / Paid Media</option>
                        <option value="SOCIAL">Social / Escucha</option>
                        <option value="TRANSVERSAL">Transversal</option>
                      </select>
                    </div>
                  </div>

                  <div className="curation-form-row">
                    <div className="curation-form-field">
                      <label>Nivel de Auditoría</label>
                      <select value={ruleLevel} onChange={(e) => setRuleLevel(e.target.value)}>
                        <option value="Per-URL SEO">Per-URL SEO</option>
                        <option value="Per-campaña Paid">Per-campaña Paid</option>
                        <option value="Torre de Control">Torre de Control</option>
                        <option value="Reporte de cuenta">Reporte de cuenta</option>
                        <option value="Jueza">Jueza</option>
                      </select>
                    </div>
                    <div className="curation-form-field">
                      <label>Alcance de Cliente</label>
                      <select value={ruleClient} onChange={(e) => setRuleClient(e.target.value)}>
                        <option value="ALL">ALL (Aplica a todos los clientes)</option>
                        {clients.map((c) => (
                          <option key={c.clientSlug} value={c.clientSlug}>
                            Solo {c.displayName} ({c.clientSlug})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="curation-form-field">
                    <label>Título de la Regla</label>
                    <input
                      type="text"
                      value={ruleTitle}
                      onChange={(e) => setRuleTitle(e.target.value)}
                      placeholder="Ej. Respetar Stack Técnico de la Web en Auditoría GEO"
                    />
                  </div>

                  <div className="curation-form-field">
                    <label>Precondición (¿Cuándo aplica o gatilla?)</label>
                    <textarea
                      rows={2}
                      value={rulePrecondition}
                      onChange={(e) => setRulePrecondition(e.target.value)}
                      placeholder="Ej. Cliente con stack Next.js/React documentado en parametros_clientes..."
                    />
                  </div>

                  <div className="curation-form-field">
                    <label>Diagnóstico Identificado</label>
                    <textarea
                      rows={2}
                      value={ruleDiagnosis}
                      onChange={(e) => setRuleDiagnosis(e.target.value)}
                      placeholder="Ej. Diagnóstico sugiere GTM cuando la web requiere JSON-LD nativo..."
                    />
                  </div>

                  <div className="curation-form-field">
                    <label>Acción Concreta y Recomendación</label>
                    <textarea
                      rows={2}
                      value={ruleAction}
                      onChange={(e) => setRuleAction(e.target.value)}
                      placeholder="Ej. Inyectar JSON-LD server-rendered y citar el repositorio del cliente..."
                    />
                  </div>

                  {/* Cláusula de guarda obligatoria */}
                  <div className="curation-guard-callout">
                    <Shield size={20} style={{ flexShrink: 0, color: "#fbbf24", marginTop: "2px" }} />
                    <div>
                      <strong style={{ display: "block", color: "#fbbf24", marginBottom: "4px" }}>
                        Cláusula de Guarda Obligatoria (No aplica si)
                      </strong>
                      <span>
                        La guarda ausente fue la causa raíz de falsos positivos en ROAS y gaps. Declara taxativamente en qué casos esta regla NO debe dispararse.
                      </span>
                    </div>
                  </div>

                  <div className="curation-form-field">
                    <label style={{ color: "#fbbf24" }}>No Aplica Si (Condición de Guarda Anti-Drift)</label>
                    <input
                      type="text"
                      value={ruleNoAplicaSi}
                      onChange={(e) => setRuleNoAplicaSi(e.target.value)}
                      placeholder="Ej. No aplica si el cliente usa WordPress sin acceso a código fuente."
                    />
                  </div>

                  <div className="curation-form-row">
                    <div className="curation-form-field">
                      <label>Estado Inicial</label>
                      <select value={ruleStatus} onChange={(e) => setRuleStatus(e.target.value as "ACTIVA" | "A_PRUEBA")}>
                        <option value="ACTIVA">ACTIVA (Entra al System Prompt / RAG)</option>
                        <option value="A_PRUEBA">A_PRUEBA (Requiere validación empírica)</option>
                      </select>
                    </div>
                    <div className="curation-form-field">
                      <label>Ticket de Feedback Origen</label>
                      <input
                        type="text"
                        disabled
                        value={selectedFeedbackForPromotion?.feedback_id || "Creación manual"}
                        style={{ opacity: 0.6 }}
                      />
                    </div>
                  </div>
                </div>

                <div className="curation-modal-footer">
                  <button
                    type="button"
                    className="curation-btn"
                    style={{ background: "rgba(255,255,255,0.06)", color: "var(--muted, #94a3b8)" }}
                    disabled={savingRule}
                    onClick={() => setPromoteModalOpen(false)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="curation-btn btn-promote"
                    disabled={savingRule}
                    onClick={handleSaveRule}
                  >
                    {savingRule ? (
                      <>Guardando en BigQuery...</>
                    ) : (
                      <>
                        <ShieldCheck size={14} /> Guardar y Formalizar Regla
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
